import React, { useEffect, useState } from 'react';
import FindInviteSuccessForm from '@apps/guest/packages/layout-engine/components/UnlockDialog/FindInviteSuccessForm/FindInviteSuccessForm';
import { VerifyEmailForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/VerifyEmailForm';
import { FindInviteForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/FindInviteForm';
import { AskHostForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AskHostForm';
import { AskHostSuccessForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AskHostSuccessForm';
import { UnlockForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockForm';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.context';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { GuestTypes } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import type { ManageUnlockDialogFormsProps } from '@apps/guest/packages/layout-engine/components/UnlockDialog/ManageUnlockDialogForms/ManageUnlockDialogForms.types';

export const ManageUnlockDialogForms: React.FC<ManageUnlockDialogFormsProps> = ({ unlockData, onDialogClose }) => {
  const { eventId, hasUnlocked, shouldSkipPasswordGate, setShowBackButton, onBackButtonClick, shouldDisplayFindInvite } = unlockData ?? {};

  const [showAskHost, setShowAskHost] = useState<boolean>(false);
  const [showAskHostSuccess, setShowAskHostSuccess] = useState<boolean>(false);
  const [guest, setGuest] = useState<GuestTypes>({ firstName: '', lastName: '', personIdTag: '' });
  const [guestsFound, setGuestsFound] = useState<number>(0);

  const { tq } = useTranslation('guestUnlock');

  const { toast } = useToast();
  const { markUnlocked } = useUnlockDialogContext();

  // Manage onClick Dialog Back button
  useEffect(() => {
    onBackButtonClick(() => {
      setShowAskHost(false);
      setGuestsFound(0);
    });
  }, [onBackButtonClick]);

  // Manage Dialog Back button conditions
  useEffect(() => {
    setShowBackButton(false);
    if (guestsFound > 1 && !guest.personIdTag) setShowBackButton(true);
    if (showAskHost) setShowBackButton(true);
  }, [guest.personIdTag, guestsFound, setShowBackButton, showAskHost]);

  // CB Only show UnlockSuccess toast if we don't have to display FindInvite
  const unlockSuccessHandle = () => {
    // Flag the unlock for both flows; the dialog provider's close wrapper
    // reads this and triggers a full page reload when the dialog finally closes.
    markUnlocked();
    if (!shouldDisplayFindInvite) {
      hideUnlockSuccessHandle();
      toast(tq`🙌 Yay! You can now access all event details.`, { position: 'bottom-center' });
    }
  };

  // CB Only works if we don't have to display FindInvite
  const hideUnlockSuccessHandle = () => {
    if (!shouldDisplayFindInvite) {
      onDialogClose();
    }
  };

  // If we have a personIdTag it means that we have already unlocked the invitation
  if (guest.personIdTag) {
    return (
      <FindInviteSuccessForm
        eventId={eventId}
        guest={guest}
        onDialogClose={onDialogClose}
      />
    );
  }

  // If we have more thant 1 guest found, it means that we have to verify the email.
  if (guestsFound > 1) {
    return (
      <VerifyEmailForm
        eventId={eventId}
        guest={guest}
        setGuest={setGuest}
      />
    );
  }

  if (shouldDisplayFindInvite && (hasUnlocked || shouldSkipPasswordGate)) {
    return (
      <FindInviteForm
        eventId={eventId}
        setGuestsFound={setGuestsFound}
        setGuest={setGuest}
      />
    );
  }

  // showAskHost Section
  if (showAskHost) {
    const onCloseHandler = () => {
      setShowAskHost(false);
      setShowAskHostSuccess(true);
    };
    return (
      <AskHostForm
        eventId={eventId}
        onClose={onCloseHandler}
      />
    );
  }

  if (showAskHostSuccess) {
    return <AskHostSuccessForm onClose={onDialogClose} />;
  }

  // Show password form to unlock the event
  return (
    <UnlockForm
      eventId={eventId}
      onUnlockSuccess={unlockSuccessHandle}
      onShowAskHost={() => setShowAskHost(true)}
    />
  );
};
